import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quotesService } from '../../services/quotesService';
import './QuotesList.css';

const QuotesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedQuoteForConversion, setSelectedQuoteForConversion] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await quotesService.getAll();
      setQuotes(data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) {
      return;
    }

    try {
      await quotesService.delete(quoteId);
      setQuotes(quotes.filter(q => q.id !== quoteId));
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await quotesService.send(quoteId, {});
      // Update the quote status locally
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'sent' } : q
      ));
      alert('Quote sent successfully!');
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote');
    }
  };

  const handleConvertToItinerary = async () => {
    if (!selectedQuoteForConversion) return;

    try {
      setConvertingQuoteId(selectedQuoteForConversion.id);
      await quotesService.convertToItinerary(selectedQuoteForConversion.id);
      
      // Update the quote status locally to show it's been converted
      setQuotes(quotes.map(q => 
        q.id === selectedQuoteForConversion.id ? { ...q, status: 'converted' } : q
      ));
      
      // Close dialog
      setShowConvertDialog(false);
      setSelectedQuoteForConversion(null);
      
      // Navigate to itineraries
      navigate('/agent/itineraries');
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.error || 'Failed to convert quote to itinerary');
    } finally {
      setConvertingQuoteId(null);
    }
  };

  const openConvertDialog = (quote) => {
    setSelectedQuoteForConversion(quote);
    setShowConvertDialog(true);
  };

  const closeConvertDialog = () => {
    setShowConvertDialog(false);
    setSelectedQuoteForConversion(null);
  };

  // Check if quote can be converted
  const canConvertToItinerary = (quote) => {
    return quote.status && 
           quote.status !== 'draft' && 
           quote.status !== 'converted' &&
           quote.status !== 'expired';
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge badge-gray',
      sent: 'badge badge-blue',
      accepted: 'badge badge-green',
      rejected: 'badge badge-red',
      expired: 'badge badge-orange',
      converted: 'badge badge-purple',
      deposit_paid: 'badge badge-teal',
      paid: 'badge badge-emerald'
    };
    return statusClasses[status] || 'badge badge-gray';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      draft: 'Draft',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      deposit_paid: 'Deposit Paid',
      paid: 'Fully Paid'
    };
    return statusTexts[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return <div className="loading">Loading quotes...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="quotes-list">
      <div className="quotes-header">
        <h1>Travel Quotes</h1>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/agent/quotes/new')}
        >
          Create New Quote
        </button>
      </div>

      <div className="quotes-filters">
        <input
          type="text"
          placeholder="Search by title, client, or quote number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
          <option value="converted">Converted</option>
          <option value="deposit_paid">Deposit Paid</option>
          <option value="paid">Fully Paid</option>
        </select>
      </div>

      {filteredQuotes.length === 0 ? (
        <div className="no-quotes">
          <p>No quotes found.</p>
          {quotes.length === 0 && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/agent/quotes/new')}
            >
              Create Your First Quote
            </button>
          )}
        </div>
      ) : (
        <div className="quotes-table-container">
          <table className="quotes-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Title</th>
                <th>Client</th>
                <th>Travel Dates</th>
                <th>Total</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map(quote => (
                <tr key={quote.id}>
                  <td>{quote.quote_number}</td>
                  <td>
                    <strong>{quote.title}</strong>
                  </td>
                  <td>
                    <div>{quote.client_name}</div>
                    <small>{quote.client_email}</small>
                  </td>
                  <td>
                    {quote.travel_start_date && quote.travel_end_date ? (
                      <span>
                        {new Date(quote.travel_start_date).toLocaleDateString()} - 
                        {new Date(quote.travel_end_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-gray">Not set</span>
                    )}
                  </td>
                  <td>
                    ${(parseFloat(quote.total_amount) || 0).toFixed(2)}
                  </td>
                  <td>
                    <span className={getStatusBadge(quote.status)}>
                      {getStatusText(quote.status)}
                    </span>
                  </td>
                  <td>
                    {new Date(quote.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate(`/agent/quotes/${quote.id}`)}
                      >
                        Edit
                      </button>
                      {quote.status === 'draft' && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSendQuote(quote.id)}
                        >
                          Send
                        </button>
                      )}
                      {canConvertToItinerary(quote) && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => openConvertDialog(quote)}
                          disabled={convertingQuoteId === quote.id}
                        >
                          {convertingQuoteId === quote.id ? 'Converting...' : 'Convert to Itinerary'}
                        </button>
                      )}
                      {quote.status === 'converted' && (
                        <span className="converted-text">
                          ✓ Converted
                        </span>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(quote.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Convert to Itinerary Confirmation Dialog */}
      {showConvertDialog && selectedQuoteForConversion && (
        <div className="modal-overlay" onClick={closeConvertDialog}>
          <div className="modal-content convert-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Convert Quote to Itinerary</h2>
              <button className="close-btn" onClick={closeConvertDialog}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="warning-message">
                <p><strong>Important:</strong></p>
                <p>This will convert your Travel Quote to an Itinerary. We recommend doing this only after you've received payment from the Client.</p>
                <p>If you don't need to receive payment for this Travel Quote or already have, please proceed.</p>
              </div>
              <div className="quote-details">
                <h3>Quote Details:</h3>
                <p><strong>Title:</strong> {selectedQuoteForConversion.title}</p>
                <p><strong>Client:</strong> {selectedQuoteForConversion.client_name}</p>
                <p><strong>Total Amount:</strong> ${(parseFloat(selectedQuoteForConversion.total_amount) || 0).toFixed(2)}</p>
                <p><strong>Status:</strong> {getStatusText(selectedQuoteForConversion.status)}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={closeConvertDialog}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleConvertToItinerary}
                disabled={convertingQuoteId === selectedQuoteForConversion.id}
              >
                {convertingQuoteId === selectedQuoteForConversion.id ? 'Converting...' : 'Confirm Conversion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesList;
